import React, { useState } from 'react';
import { formatStatusLabel } from '../../utils/helpers';
import './styles/FilterPanel.css';

const getStatusConfig = (status) => {
  const statusMap = {
    cancelled: { bgcolor: '#f44336', textColor: '#fff' },
    completed: { bgcolor: '#4caf50', textColor: '#fff' },
    'no show': { bgcolor: '#9c27b0', textColor: '#fff' },
    rescheduled: { bgcolor: '#2196f3', textColor: '#fff' },
    default: { bgcolor: '#2196f3', textColor: '#fff' },
  };
  return statusMap[status?.toLowerCase()] || statusMap.default;
};

const FilterPanel = ({
  filters,
  setFilters,
  availableServices,
  availableStatuses,
  onClearFilters,
  searchTerm,
  setSearchTerm,
  onExport,
  searchPlaceholder = 'Search by client name...',
}) => {
  const [expanded, setExpanded] = useState(false);

  const activeFiltersCount = [
    filters.dateRange[0] || filters.dateRange[1],
    ...filters.services,
    ...filters.statuses,
    searchTerm,
  ].filter(Boolean).length;

  const handleStartDateChange = (e) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    setFilters((prev) => ({ ...prev, dateRange: [date, prev.dateRange[1]] }));
  };

  const handleEndDateChange = (e) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    setFilters((prev) => ({ ...prev, dateRange: [prev.dateRange[0], date] }));
  };

  const handleServiceChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setFilters((prev) => ({ ...prev, services: selected }));
  };

  const handleStatusChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setFilters((prev) => ({ ...prev, statuses: selected }));
  };

  const handleRemoveFilter = (type, value) => {
    switch (type) {
      case 'date':
        setFilters((prev) => ({ ...prev, dateRange: [null, null] }));
        break;
      case 'service':
        setFilters((prev) => ({
          ...prev,
          services: prev.services.filter((s) => s !== value),
        }));
        break;
      case 'status':
        setFilters((prev) => ({
          ...prev,
          statuses: prev.statuses.filter((s) => s !== value),
        }));
        break;
      default:
        break;
    }
  };

  const formatDateForInput = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  };

  return (
    <div className="app-table-wrap archives-filter">
      <header className="archives-filter__header">
        <span className="archives-filter__header-title">Filters</span>
      </header>

      <div className="archives-filter__body">
        <div className="archives-filter__row">
          <div className="archives-filter__search-wrap">
            <span className="archives-filter__search-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" focusable="false">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </span>
            <input
              type="text"
              className="archives-filter__search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search"
            />
            {searchTerm && (
              <button
                type="button"
                className="archives-filter__clear-search"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            )}
          </div>
          <div className="archives-filter__actions">
            <button type="button" className="archives-filter__btn archives-filter__btn--success" onClick={onExport}>
              Export
            </button>
            <button
              type="button"
              className="archives-filter__btn archives-filter__btn--primary"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
            >
              {expanded ? 'Less Filters' : 'More Filters'}
            </button>
          </div>
        </div>

        {activeFiltersCount > 0 && (
          <div className="archives-filter__chips">
            {searchTerm && (
              <span className="archives-filter__chip archives-filter__chip--search">
                Search: {searchTerm}
                <button
                  type="button"
                  className="archives-filter__chip-remove"
                  onClick={() => setSearchTerm('')}
                  aria-label="Remove search"
                >
                  ×
                </button>
              </span>
            )}
            {filters.dateRange[0] && filters.dateRange[1] && (
              <span className="archives-filter__chip archives-filter__chip--outline">
                {filters.dateRange[0].toLocaleDateString()} – {filters.dateRange[1].toLocaleDateString()}
                <button
                  type="button"
                  className="archives-filter__chip-remove"
                  onClick={() => handleRemoveFilter('date')}
                  aria-label="Remove date range"
                >
                  ×
                </button>
              </span>
            )}
            {filters.services.map((service) => (
              <span key={`service-${service}`} className="archives-filter__chip archives-filter__chip--outline">
                {service}
                <button
                  type="button"
                  className="archives-filter__chip-remove"
                  onClick={() => handleRemoveFilter('service', service)}
                  aria-label={`Remove ${service}`}
                >
                  ×
                </button>
              </span>
            ))}
            {filters.statuses.map((status) => {
              const config = getStatusConfig(status);
              return (
                <span
                  key={`status-${status}`}
                  className="archives-filter__chip"
                  style={{ backgroundColor: config.bgcolor, color: config.textColor }}
                >
                  {formatStatusLabel(status)}
                  <button
                    type="button"
                    className="archives-filter__chip-remove"
                    onClick={() => handleRemoveFilter('status', status)}
                    aria-label={`Remove ${status}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className={`archives-filter__more ${expanded ? 'archives-filter__more--open' : ''}`}>
          <div className="archives-filter__more-inner">
            <div className="archives-filter__date-row">
              <label className="archives-filter__label">
                <span className="archives-filter__label-text">Start date</span>
                <input
                  type="date"
                  className="archives-filter__input"
                  value={formatDateForInput(filters.dateRange[0])}
                  onChange={handleStartDateChange}
                />
              </label>
              <label className="archives-filter__label">
                <span className="archives-filter__label-text">End date</span>
                <input
                  type="date"
                  className="archives-filter__input"
                  value={formatDateForInput(filters.dateRange[1])}
                  onChange={handleEndDateChange}
                  min={formatDateForInput(filters.dateRange[0])}
                />
              </label>
            </div>

            <div className="archives-filter__services-status-row">
              {availableServices.length > 0 && (
                <label className="archives-filter__label archives-filter__label--block archives-filter__label--row-item">
                  <span className="archives-filter__label-text">Services</span>
                  <select
                    multiple
                    size={Math.min(availableServices.length, 10)}
                    className="archives-filter__select"
                    value={filters.services}
                    onChange={handleServiceChange}
                    aria-label="Filter by services"
                  >
                    {availableServices.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                  <span className="archives-filter__select-hint">Hold Ctrl/Cmd to select multiple</span>
                </label>
              )}

              {availableStatuses.length > 0 && (
                <label className="archives-filter__label archives-filter__label--block archives-filter__label--row-item">
                  <span className="archives-filter__label-text">Status</span>
                  <select
                    multiple
                    size={Math.min(availableStatuses.length, 6)}
                    className="archives-filter__select"
                    value={filters.statuses}
                    onChange={handleStatusChange}
                    aria-label="Filter by status"
                  >
                    {availableStatuses.map((status) => (
                      <option key={status} value={status}>
                        {formatStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                  <span className="archives-filter__select-hint">Hold Ctrl/Cmd to select multiple</span>
                </label>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
